import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  LinearProgress, List, ListItemButton, ListItemText, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopIcon from '@mui/icons-material/Stop';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import {
  NEIGHBOR_SCAN_OPEN_EVENT, NEIGHBOR_SCAN_STARTED_EVENT, NEIGHBOR_SCAN_STATUS, describeDispatch, describeScanOutcome,
  describeScanPlan, describeSiteAtStart, filterScanDevices, rescanCandidates, scanOpenCount, scanProgressPct,
  sortScanDevices,
} from '../utils/neighborScanTracking';

const POLL_MS = 5000;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString() : '—');

function StatusChip({ status }) {
  const meta = NEIGHBOR_SCAN_STATUS[status] || { label: status, color: 'default', help: '' };
  return (
    <Tooltip title={meta.help}>
      <Chip
        size="small"
        label={meta.label}
        color={meta.color}
        variant={status === 'not_sent' || status === 'queued' || status === 'unconfirmed' ? 'outlined' : 'filled'}
      />
    </Tooltip>
  );
}

function SummaryChips({ summary }) {
  if (!summary) return null;
  const chip = (key, n, color, variant = 'filled') => (n > 0
    ? <Chip key={key} size="small" color={color} variant={variant} label={`${n} ${NEIGHBOR_SCAN_STATUS[key].label.toLowerCase()}`} />
    : null);
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      <Chip size="small" color="success" label={`${summary.received} got results`} />
      {chip('scanning', summary.scanning, 'warning')}
      {chip('sent', summary.sent, 'info')}
      {chip('unconfirmed', summary.unconfirmed, 'default', 'outlined')}
      {chip('queued', summary.queued, 'default', 'outlined')}
      {chip('overdue', summary.overdue, 'error')}
      {chip('not_sent', summary.not_sent, 'default', 'outlined')}
    </Stack>
  );
}

/** Recent multi-device scans; pick one to track. */
function ScanList({ onPick }) {
  const [scans, setScans] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      setError('');
      setScans(await api.listNeighborScans(20));
    } catch (err) {
      setError(err?.message || 'Failed to load scans');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <Alert severity="error" action={<Button size="small" onClick={load}>Retry</Button>}>{error}</Alert>;
  if (!scans) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  if (scans.length === 0) {
    return <Typography color="text.secondary" sx={{ py: 3 }}>No multi-device scans in the last 30 days.</Typography>;
  }
  return (
    <List dense disablePadding>
      {scans.map((s) => (
        <ListItemButton key={s.id} onClick={() => onPick(s.id)} divider>
          <ListItemText
            primary={`${fmt(s.created_at)} · ${s.summary.targeted} device${s.summary.targeted === 1 ? '' : 's'}${s.source === 'all' ? ' (whole site)' : ''}${s.batch_size > 0 ? ` · ${s.batch_size} at a time` : ''}`}
            secondary={`${s.created_by ? `${s.created_by} · ` : ''}${s.chan_scan_interval}s per channel · ${s.all_freq ? 'all channels' : 'known channels'}${describeSiteAtStart(s) ? ` · ${describeSiteAtStart(s)}` : ''}${s.status === 'active' ? ' · in progress' : s.status === 'cancelled' ? ' · cancelled' : ''}`}
          />
          <Box sx={{ ml: 2, minWidth: 260 }}><SummaryChips summary={s.summary} /></Box>
        </ListItemButton>
      ))}
    </List>
  );
}

/** One scan: what is happening, progress and each device's state; refreshed while it runs. */
function ScanDetail({ scanId, onRescanned }) {
  const { enqueueSnackbar } = useSnackbar();
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ column: 'status', dir: 'asc' });
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState('');
  const sortBy = (column) => setSort((s) => ({ column, dir: s.column === column && s.dir === 'asc' ? 'desc' : 'asc' }));
  const header = (column, label, align) => (
    <TableCell align={align} sortDirection={sort.column === column ? sort.dir : false}>
      <TableSortLabel active={sort.column === column} direction={sort.column === column ? sort.dir : 'asc'} onClick={() => sortBy(column)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      setError('');
      setView(await api.getNeighborScan(scanId));
    } catch (err) {
      setError(err?.message || 'Failed to load the scan');
    } finally {
      setRefreshing(false);
    }
  }, [scanId]);

  useEffect(() => {
    setView(null);
    setFilter('all');
    load();
  }, [load]);
  const active = view?.status === 'active' || (view && !view.done);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [active, load]);

  const again = rescanCandidates(view);
  const rescan = async () => {
    if (again.length === 0) return;
    setBusy('rescan');
    try {
      const result = await api.startNeighborsScanBatch(again, view.chan_scan_interval, view.all_freq, view.batch_size);
      if (result?.scan_id) onRescanned(result.scan_id);
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to start the scan', { variant: 'error' });
    } finally {
      setBusy('');
    }
  };
  const cancel = async () => {
    setBusy('cancel');
    try {
      await api.cancelNeighborScan(scanId);
      await load();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to stop the scan', { variant: 'error' });
    } finally {
      setBusy('');
    }
  };
  const copyMissing = async () => {
    const serials = filterScanDevices(view, 'missing').map((d) => d.serial);
    try {
      await navigator.clipboard.writeText(serials.join('\n'));
      enqueueSnackbar(`Copied ${serials.length} serial${serials.length === 1 ? '' : 's'}`, { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not copy to the clipboard', { variant: 'error' });
    }
  };

  if (error && !view) return <Alert severity="error" action={<Button size="small" onClick={load}>Retry</Button>}>{error}</Alert>;
  if (!view) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  const { summary } = view;
  const rows = sortScanDevices(filterScanDevices(view, filter), sort.column, sort.dir);
  const open = scanOpenCount(summary);
  const missingCount = (summary.overdue || 0) + (summary.unconfirmed || 0);
  const activeCount = open;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body2" color="text.secondary">
          Started {fmt(view.created_at)}{view.created_by ? ` by ${view.created_by}` : ''}
          {view.completed_at ? ` · ended ${fmtTime(view.completed_at)}` : ''}
          {describeSiteAtStart(view) ? ` · ${describeSiteAtStart(view)}` : ''}
        </Typography>
        <Alert severity={active ? 'info' : summary.overdue > 0 ? 'warning' : 'success'} icon={false} sx={{ mt: 1 }}>
          <Typography variant="body2">
            {active ? describeScanPlan(view) : `${describeScanOutcome(view)} ${describeScanPlan(view)}`}
          </Typography>
        </Alert>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={scanProgressPct(summary)}
              color={summary.overdue > 0 ? 'warning' : 'success'}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          <Typography variant="body2" sx={{ minWidth: 190, textAlign: 'right' }}>
            <strong>{summary.received}</strong> of {summary.targeted} got results
            {open > 0 ? ` · ${open} to go` : ''}
          </Typography>
          <Tooltip title={active ? `Refreshing every ${POLL_MS / 1000}s` : 'Refresh'}>
            <span>
              <IconButton size="small" onClick={load} disabled={refreshing}>
                {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Box sx={{ mt: 1 }}><SummaryChips summary={summary} /></Box>
      </Box>
      {summary.overdue > 0 && (
        <Alert severity="warning">
          {summary.overdue} device{summary.overdue === 1 ? ' has' : 's have'} not reported by {summary.overdue === 1 ? 'its' : 'their'} deadline.
          A report that still arrives is picked up here.
        </Alert>
      )}
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_e, v) => v && setFilter(v)}>
          <ToggleButton value="all" sx={{ textTransform: 'none' }}>All ({summary.targeted})</ToggleButton>
          <ToggleButton value="active" sx={{ textTransform: 'none' }}>In progress ({activeCount})</ToggleButton>
          <ToggleButton value="received" sx={{ textTransform: 'none' }}>Got results ({summary.received})</ToggleButton>
          <ToggleButton value="missing" sx={{ textTransform: 'none' }}>No results ({missingCount})</ToggleButton>
          <ToggleButton value="not_sent" sx={{ textTransform: 'none' }}>Not sent ({summary.not_sent})</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        {active && (
          <Tooltip title="Devices still waiting for their turn are not scanned. Devices already scanning finish on their own and their reports still count.">
            <span>
              <Button size="small" color="error" variant="outlined" startIcon={<StopIcon />} onClick={cancel} disabled={busy !== ''}>
                {busy === 'cancel' ? 'Stopping…' : 'Stop scan'}
              </Button>
            </span>
          </Tooltip>
        )}
        <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyMissing} disabled={missingCount === 0}>
          Copy missing
        </Button>
        <Tooltip title="Starts a new scan, with the same settings, on devices without results and on devices the command did not reach.">
          <span>
            <Button size="small" variant="contained" onClick={rescan} disabled={again.length === 0 || busy !== '' || active}>
              {busy === 'rescan' ? 'Starting…' : `Scan again (${again.length})`}
            </Button>
          </span>
        </Tooltip>
      </Stack>
      <TableContainer sx={{ maxHeight: '45vh' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {header('device', 'Device')}
              {header('status', 'Status')}
              {header('sent', 'Sent')}
              <TableCell>Confirmed</TableCell>
              {header('report', 'Report')}
              {header('neighbors', 'Neighbors', 'right')}
              <TableCell>Now</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((d) => (
              <TableRow key={d.serial} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{d.serial}</Typography>
                  {d.name && <Typography variant="caption" color="text.secondary">{d.name}</Typography>}
                </TableCell>
                <TableCell>
                  <StatusChip status={d.status} />
                  {d.status === 'not_sent' && (
                    <Typography variant="caption" color="text.secondary" display="block">{describeDispatch(d)}</Typography>
                  )}
                  {(d.status === 'scanning' || d.status === 'sent' || d.status === 'unconfirmed') && d.overdue_at && (
                    <Typography variant="caption" color="text.secondary" display="block">report expected by {fmtTime(d.overdue_at)}</Typography>
                  )}
                </TableCell>
                <TableCell>{d.sent_at ? fmtTime(d.sent_at) : '—'}</TableCell>
                <TableCell>{d.acked_at ? fmtTime(d.acked_at) : d.status === 'unconfirmed' ? 'no' : '—'}</TableCell>
                <TableCell>{d.report_at ? fmtTime(d.report_at) : '—'}</TableCell>
                <TableCell align="right">{d.status === 'received' ? d.neighbors ?? 0 : '—'}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" color={d.connected ? 'success' : 'default'} label={d.connected ? 'Online' : 'Offline'} />
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No devices.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

/**
 * Neighbors scan tracker, mounted once inside the snackbar provider. It
 * opens on every multi-device scan the app starts (the scan API calls
 * announce them) and on request (openNeighborScanTracker).
 */
export default function NeighborScanTrackerHost() {
  const [open, setOpen] = useState(false);
  const [scanId, setScanId] = useState(null);
  const openRef = useRef(false);
  openRef.current = open;

  useEffect(() => {
    const onOpen = (e) => {
      setScanId(e.detail?.scanId || null);
      setOpen(true);
    };
    const onStarted = (e) => {
      const id = e.detail?.scanId;
      if (!id) return;
      // A scan started from inside the tracker (Scan again) is shown by
      // the tracker itself; one a mounted Recent Scans view took is
      // shown there.
      if (openRef.current || e.detail?.handled) return;
      setScanId(id);
      setOpen(true);
    };
    window.addEventListener(NEIGHBOR_SCAN_OPEN_EVENT, onOpen);
    window.addEventListener(NEIGHBOR_SCAN_STARTED_EVENT, onStarted);
    return () => {
      window.removeEventListener(NEIGHBOR_SCAN_OPEN_EVENT, onOpen);
      window.removeEventListener(NEIGHBOR_SCAN_STARTED_EVENT, onStarted);
    };
  }, []);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {scanId && (
          <IconButton size="small" onClick={() => setScanId(null)} aria-label="All scans">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        {scanId ? 'Neighbors scan' : 'Recent neighbors scans'}
      </DialogTitle>
      <DialogContent dividers>
        {open && (scanId
          ? <ScanDetail scanId={scanId} onRescanned={setScanId} />
          : <ScanList onPick={setScanId} />)}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
